/**
 * The Building Session's Save App toasts. A save that went live says who can
 * open it, from the server-derived audience (FRONT-1879), falling back to the
 * smallest audience when the field is missing so the toast never throws. A
 * save that published through Git also offers Undo for that publish
 * (FRONT-3368). A save that waits for Publish offers Publish instead.
 */
import { toast } from 'sonner';
import type { QueryClient } from '@tanstack/react-query';
import { APP_AUDIENCE_COPY } from './coding-events';
import { openAppUrl } from '~/lib/open-app-url';
import { publishUndoToast } from '~/features/apps/publish-undo';
import { publishSavedChanges } from '~/features/apps/publish-changes';
import type { useConfirm } from '~/components/confirm-dialog';
import type { PublishCodingSessionResult } from '~/hooks/mutations/use-coding-mutations';

export const SAVE_APP_TOOLTIP =
  'Save App — stores this project in your Apps and makes it live at its address; who can open it is set by the Share control';
export const SAVE_APP_WAITS_TOOLTIP =
  "Save App — saves your changes to this app without changing what's live; press Publish to make it live. Who can open it is set by the Share control";
export const SAVE_APP_UNKNOWN_TOOLTIP = 'Save App — saves your changes to this app; who can open it is set by the Share control';

/** The Save App disc's hint; it claims "live" only when the server says so, since a failed lookup leaves the field out. */
export function saveAppTooltip(saveGoesLive: boolean | undefined): string {
  if (saveGoesLive === undefined) return SAVE_APP_UNKNOWN_TOOLTIP;
  return saveGoesLive ? SAVE_APP_TOOLTIP : SAVE_APP_WAITS_TOOLTIP;
}

export const SAVED_NOT_LIVE_TOAST = "Saved. It isn't live yet.";
export const SAVED_NOTHING_NEW_TOAST = 'Saved. Nothing new to publish.';

/** What a waiting save's Publish needs from the window it was pressed in. */
export interface SavedAppPublish {
  confirm: ReturnType<typeof useConfirm>;
  previewOn: boolean;
  onPublished?: () => void;
}

export function announceSavedApp(qc: QueryClient, result: PublishCodingSessionResult, publish: SavedAppPublish): void {
  const undo = publishUndoToast(qc, result.slug, result, 'cancel');
  if (result.wentLive === false) {
    if (result.waiting) {
      const goingLive = result.status !== 'live';
      toast.success(SAVED_NOT_LIVE_TOAST, {
        description: goingLive
          ? `“${result.title}” is saved and offline. Publish shows what goes live, then makes it live.`
          : `Visitors still see the previous version of “${result.title}”. Publish shows what goes live first.`,
        action: { label: 'Publish', onClick: () => void publishSavedChanges({ qc, slug: result.slug, goingLive, ...publish }) },
        duration: 8000,
        ...undo,
      });
    } else {
      toast.success(SAVED_NOTHING_NEW_TOAST, {
        description: `Everyone who can open “${result.title}” already sees this version.`,
        duration: 8000,
        ...undo,
      });
    }
  } else {
    const audienceHint = (APP_AUDIENCE_COPY[result.audience] ?? APP_AUDIENCE_COPY['only-you']).hint;
    toast.success(result.updated ? 'App updated' : 'Saved to Apps', {
      description: result.updated ? `“${result.title}” now holds your latest changes. ${audienceHint}` : `“${result.title}” is in your Apps. ${audienceHint}`,
      action: { label: 'Open app', onClick: () => openAppUrl(result.url) },
      duration: 8000,
      ...undo,
    });
  }
  // Informational: two files were kept out on purpose, the app published fine (FRONT-2027).
  if (result.warnings.length > 0) toast.info(result.warnings[0], { duration: 8000 });
}
